import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type ChecklistItemConversionRoute = {
  owner: string
  repository: string
  issueNumber: number
}

type GraphQLError = {message?: unknown}
type GraphQLEnvelope<TData> = {data?: TData | null; errors?: unknown}

function errorMessage(errors: unknown, fallback: string): string | undefined {
  if (errors == null) return undefined
  if (!Array.isArray(errors)) return fallback
  if (errors.length === 0) return undefined

  const messages = errors
    .map(error => (typeof (error as GraphQLError)?.message === 'string' ? (error as GraphQLError).message : null))
    .filter((message): message is string => Boolean(message))
  return messages.length > 0 ? messages.join(', ') : fallback
}

export async function postChecklistItemConversion<TData, TInput extends object>(
  route: ChecklistItemConversionRoute,
  endpoint: 'checklist_item_issues' | 'checklist_item_sub_issues',
  input: TInput,
  queryId: string,
): Promise<TData> {
  const owner = encodeURIComponent(route.owner)
  const repository = encodeURIComponent(route.repository)
  const path = `/${owner}/${repository}/issues/${route.issueNumber}/${endpoint}?queryId=${encodeURIComponent(queryId)}`
  const response = await reactFetchJSON(path, {method: 'POST', body: {input}})

  if (!response.ok) throw new Error(`Checklist item conversion failed with HTTP ${response.status}`)

  let json: unknown
  try {
    json = await response.json()
  } catch {
    throw new Error('Checklist item conversion did not return valid JSON')
  }

  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    throw new Error('Checklist item conversion returned an invalid response envelope')
  }

  const envelope = json as GraphQLEnvelope<TData>
  // Relay surfaced usable partial responses, so preserve that behavior: when GraphQL returns a
  // `data` payload alongside top-level `errors`, keep the data and let the operation-specific
  // validators enforce payload errors and required settlement fields. Top-level errors must never
  // override usable data. Only fall back to throwing when there is no data to work with.
  if (envelope.data != null) return envelope.data
  const topLevelError = errorMessage(envelope.errors, 'Checklist item conversion returned GraphQL errors')
  throw new Error(topLevelError ?? 'Checklist item conversion returned null data')
}

export function throwPayloadErrors(errors: unknown, payloadName: string): void {
  const message = errorMessage(errors, `${payloadName} returned payload errors`)
  if (message) throw new Error(message)
}

export type IssueBodyFields = {
  readonly id: string
  readonly body: string
  readonly bodyHTML: string
  readonly bodyVersion?: string
}

export async function settleIssueFields(operation: SetFieldsOperation): Promise<void> {
  await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
}
